// src/redux/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authService } from '../../services/authService.js'
import { handleApiError } from '../../utils/handleApiError.js'
import { toast } from 'react-hot-toast'

// Helper function to check and parse localStorage safely
const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  } catch (error) {
    console.error('Failed to parse user from localStorage:', error)
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    return null
  }
}

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)
      const { data } = response

      if (data.token && data.user) {
        localStorage.setItem('token', data.token)
        localStorage.setItem('user', JSON.stringify(data.user))
        return data // Return the data object directly
      } else {
        throw new Error('Invalid response structure from server')
      }
    } catch (error) {
      const errorMessage = handleApiError(error)
      toast.error(errorMessage)
      return rejectWithValue(errorMessage)
    }
  }
)

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser()
      return response.data // Return data directly
    } catch (error) {
      const errorMessage = handleApiError(error)
      // Only show toast if it's not a 401 (handled by interceptor)
      if (error.response?.status !== 401) {
        toast.error(errorMessage)
      }
      return rejectWithValue(errorMessage)
    }
  }
)

export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser()
      return response.data
    } catch (error) {
      const errorMessage = handleApiError(error)
      return rejectWithValue(errorMessage)
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: getStoredUser(),
    token: localStorage.getItem('token') || null,
    isAuthenticated: !!localStorage.getItem('token'),
    loading: false,
    error: null,
    initialized: false
  },
  reducers: {
    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      toast.success('Logged out successfully')
    },
    clearError: (state) => {
      state.error = null
    },
    // Add this to synchronize auth state on app load
    initializeAuth: (state) => {
      state.user = getStoredUser()
      state.token = localStorage.getItem('token')
      state.isAuthenticated = !!state.token
      state.initialized = true
    },
    // Action to set loading state manually
    setLoading: (state, action) => {
      state.loading = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        // Clear invalid auth data on login failure
        if (!state.token) {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
        }
      })
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
        // Update localStorage with fresh user data
        localStorage.setItem('user', JSON.stringify(action.payload))
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.user = null
        state.token = null
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      })
      // Validate Token
      .addCase(validateToken.pending, (state) => {
        state.loading = true
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.initialized = true
        // Update localStorage with fresh user data
        localStorage.setItem('user', JSON.stringify(action.payload))
      })
      .addCase(validateToken.rejected, (state) => {
        state.loading = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.initialized = true
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      })
  }
})

export const { logout, clearError, initializeAuth, setLoading } = authSlice.actions
export default authSlice.reducer